import React, { useCallback, useEffect } from 'react';
import { array, bool, func, shape, string } from 'prop-types';
import useApi from '../../../hooks/useApi';
import template from 'url-template';
import { toSentence } from '../../../helpers/strings';

import TagSelect from '../../FormFields/TagSelect';
import {
    isTargetSelected,
    optionsTransform,
    targetChangeHandler,
    RULE_TARGET_TO_TYPE
} from './targetMappings';

const BusinessTypesUrl = template.parse('/business-types{?limit}');
const ClientsUrl =
    template.parse('/companies/{companyId}/clients{?limit,sort}');
const LIMIT = 10000;

const ClientsTargetSelect = ({
    companyId,
    disabled,
    onPatch,
    values,
    ...props
}) => {
    const [businessTypes, getBusinessTypes] = useApi(() => ({
        url: BusinessTypesUrl.expand({ limit: LIMIT })
    }));
    const [clients, getClients] = useApi(() => ({
        url: ClientsUrl.expand({
            companyId,
            limit: LIMIT,
            sort:  'name^asc'
        })
    }));

    useEffect(() => {
        getBusinessTypes();
        getClients();

        return () => {
            businessTypes.cancel();
            clients.cancel();
        };
    }, []);

    const handleChange = useCallback(
        targetChangeHandler(onPatch, {
            targetBusinessTypes: [],
            targetClients:       []
        })
    );

    const options = [
        {
            error:     businessTypes.error,
            isLoading: businessTypes.loading,
            items:     optionsTransform(businessTypes.data),
            name:      'Business Types',
            itemProps: {
                type: RULE_TARGET_TO_TYPE.targetBusinessTypes
            }
        },
        {
            error:     clients.error,
            isLoading: clients.loading,
            items:     optionsTransform(clients.data),
            name:      'Clients',
            itemProps: {
                type: RULE_TARGET_TO_TYPE.targetClients
            }
        }
    ];

    const value = [...values.targetBusinessTypes, ...values.targetClients];
    const hasError = Boolean(businessTypes.error) || Boolean(clients.error);
    const errorText = toSentence([businessTypes.error, clients.error]);

    return (
        <TagSelect
            disabled={disabled}
            error={hasError}
            grouped
            helperText={errorText}
            isItemSelected={isTargetSelected}
            kickerText="Add any combination of Client Business Types or Specific Clients that this Rule will be applied to"
            label="Clients"
            name="clients-target"
            onChange={handleChange}
            options={options}
            placeholder="Add Clients"
            value={value}
            {...props}
        />
    );
};

ClientsTargetSelect.defaultProps = {
    disabled: false,
    onPatch:  () => {},
    values:   {
        targetBusinessTypes: [],
        targetClients:       []
    }
};

ClientsTargetSelect.propTypes = {
    disabled:  bool,
    companyId: string.isRequired,
    onPatch:   func,
    values:    shape({
        targetBusinessTypes: array.isRequired,
        targetClients:       array.isRequired
    })
};

export default ClientsTargetSelect;
